import {
  Box,
  Button,
  Snackbar,
  Alert,
  RadioGroup,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { RadioInput } from 'components/cuffa/RadioInput';
import { CuffaLandCategoryEnum } from 'interfaces/cuffa.interface';
import { useCategoriaFundiaria } from './useCategoriaFundiaria';

/**
 * Mapeamento de categorias fundiárias para labels em português
 */
const CuffaLandCategoryLabels = {
  [CuffaLandCategoryEnum.PROPRIEDADE_RURAL_PARTICULAR]: 'Propriedade rural particular',
  [CuffaLandCategoryEnum.PROPRIEDADE_RURAL_ARRENDADA]: 'Propriedade rural arrendada',
  [CuffaLandCategoryEnum.POSSE]: 'Posse',
  [CuffaLandCategoryEnum.PRODUTOR_MEEIRO]: 'Produtor meeiro',
  [CuffaLandCategoryEnum.AREA_DE_USO_COLETIVO_DA_TERRA]: 'Área de uso coletivo da terra',
  [CuffaLandCategoryEnum.ASSENTAMENTO_FEDERAL_ESTADUAL]: 'Assentamento Federal/Estadual',
  [CuffaLandCategoryEnum.PNCF_TERRA_BRASIL_BANCO_DA_TERRA]: 'PNCF/Terra Brasil/Banco da Terra',
  [CuffaLandCategoryEnum.COMUNIDADE_QUILOMBOLA]: 'Comunidade Quilombola',
  [CuffaLandCategoryEnum.COMUNIDADE_TRADICIONAL]: 'Comunidade Tradicional',
  [CuffaLandCategoryEnum.TERRA_INDIGENA]: 'Terra Indígena',
};

/**
 * Componente para cadastro e edição de Categoria Fundiária
 * Funciona tanto para inclusão quanto edição de dados existentes
 */
export const CategoriaFundiariaIncluir: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  const {
    formik,
    loading,
    saving,
    openSnackbar,
    snackbarMessage,
    snackbarError,
    handleCloseSnackbar,
    handleSubmit,
    advanceNext,
  } = useCategoriaFundiaria();

  /**
   * Opções de rádio para categorias sem campos condicionais
   */
  const simpleRadios = [
    CuffaLandCategoryEnum.PROPRIEDADE_RURAL_PARTICULAR,
    CuffaLandCategoryEnum.PROPRIEDADE_RURAL_ARRENDADA,
    CuffaLandCategoryEnum.POSSE,
    CuffaLandCategoryEnum.PRODUTOR_MEEIRO,
    CuffaLandCategoryEnum.AREA_DE_USO_COLETIVO_DA_TERRA,
  ].map(value => ({
    value,
    label: CuffaLandCategoryLabels[value]
  }));

  // Exibe loading enquanto carrega dados
  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Categoria Fundiária" showBackButton />
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header
        title="Categoria Fundiária"
        showBackButton />

      <Box display="flex" justifyContent="center" mt={4}>
        <Box sx={{ maxWidth: 1152, width: '100%', p: 4 }}>
          <ContainerForm
            title="Categoria Fundiária"
            subtitle="Qual a categoria fundiária da unidade familiar?"
          >
            <form onSubmit={formik.handleSubmit}>
              <RadioGroup
                name="landCategory"
                value={formik.values.landCategory}
                onChange={formik.handleChange}
              >
                {/* Categorias simples sem campos condicionais */}
                {simpleRadios.map(({ value, label }) => (
                  <RadioInput
                    key={value}
                    value={value}
                    label={label}
                    formik={formik}
                  />
                ))}

                {/* Categorias com campos condicionais */}
                <RadioInput
                  label={CuffaLandCategoryLabels[CuffaLandCategoryEnum.ASSENTAMENTO_FEDERAL_ESTADUAL]}
                  value={CuffaLandCategoryEnum.ASSENTAMENTO_FEDERAL_ESTADUAL}
                  formik={formik}
                  conditionalFieldName={CuffaLandCategoryEnum.ASSENTAMENTO_FEDERAL_ESTADUAL}
                  conditionalFieldLabel="Código do Assentamento (formato MT00000000)"
                  conditionalFieldFormikName="codigoAssentamento"
                />

                <RadioInput
                  label={CuffaLandCategoryLabels[CuffaLandCategoryEnum.PNCF_TERRA_BRASIL_BANCO_DA_TERRA]}
                  value={CuffaLandCategoryEnum.PNCF_TERRA_BRASIL_BANCO_DA_TERRA}
                  formik={formik}
                  conditionalFieldName={CuffaLandCategoryEnum.PNCF_TERRA_BRASIL_BANCO_DA_TERRA}
                  conditionalFieldLabel="Nome do PNCF/Terra Brasil/Banco da Terra*"
                  conditionalFieldFormikName="nomePncFTerraBrasilBancoDaTerra"
                />

                <RadioInput
                  label={CuffaLandCategoryLabels[CuffaLandCategoryEnum.COMUNIDADE_QUILOMBOLA]}
                  value={CuffaLandCategoryEnum.COMUNIDADE_QUILOMBOLA}
                  formik={formik}
                  conditionalFieldName={CuffaLandCategoryEnum.COMUNIDADE_QUILOMBOLA}
                  conditionalFieldLabel="Nome da Comunidade Quilombola *"
                  conditionalFieldFormikName="nomeComunidadeQuilombola"
                />

                <RadioInput
                  label={CuffaLandCategoryLabels[CuffaLandCategoryEnum.COMUNIDADE_TRADICIONAL]}
                  value={CuffaLandCategoryEnum.COMUNIDADE_TRADICIONAL}
                  formik={formik}
                  conditionalFieldName={CuffaLandCategoryEnum.COMUNIDADE_TRADICIONAL}
                  conditionalFieldLabel="Nome da Comunidade Tradicional *"
                  conditionalFieldFormikName="nomeComunidadeTradicional"
                />

                <RadioInput
                  label={CuffaLandCategoryLabels[CuffaLandCategoryEnum.TERRA_INDIGENA]}
                  value={CuffaLandCategoryEnum.TERRA_INDIGENA}
                  formik={formik}
                  conditionalFieldName={CuffaLandCategoryEnum.TERRA_INDIGENA}
                  conditionalFieldLabel="Nome da Terra Indígena *"
                  conditionalFieldFormikName="nomeTerraIndigena"
                />
              </RadioGroup>

              {formik.touched.landCategory && formik.errors.landCategory && (
                <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                  {formik.errors.landCategory}
                </Typography>
              )}

              <Box display="flex" justifyContent="center" mt={4} gap={2} flexWrap="wrap">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate(ROUTES.VISITAS_INSTITUICOES_CUFFA_INCLUIR.replace(':uuid', uuid ?? ''))}
                  disabled={saving}
                  sx={{ minWidth: 120 }}
                >
                  Retornar
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleSubmit(false)}
                  disabled={saving}
                  sx={{ minWidth: 120 }}
                >
                  {saving && !advanceNext.current ? (
                    <CircularProgress size={20} />
                  ) : 'Salvar'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSubmit(true)}
                  disabled={saving}
                  sx={{ minWidth: 120 }}
                >
                  {saving && advanceNext.current ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : null}
                  Avançar
                </Button>
              </Box>
            </form>
          </ContainerForm>
        </Box>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbarError ? 'error' : 'success'}
          sx={{ width: '100%' }}
          onClose={handleCloseSnackbar}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
};

export default CategoriaFundiariaIncluir;
